import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Clock, Repeat, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

export default function FridgeNudge({ activeId, onSwapped, compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [swapping, setSwapping] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get(`/kitchen/nudges/${activeId}`); setData(data); } catch { /* optional */ }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  if (!data?.expiring?.length) return null;

  const swap = async () => {
    if (!data.swap_day || !data.meal) return;
    setSwapping(true);
    try {
      const { data: plan } = await api.post("/plan/swap", { household_id: activeId, day: data.swap_day });
      toast.success(`${data.swap_day} swapped to use things up 🙌`);
      onSwapped?.(plan);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSwapping(false); }
  };

  const label = (i) => (i.days_left < 0 ? "past its best" : i.days_left === 0 ? "use today" : i.days_left === 1 ? "1 day left" : `${i.days_left} days left`);

  return (
    <div className="bg-[#FBE6DC] rounded-3xl border-2 border-white/60 p-4 shadow-soft" data-testid="fridge-nudge">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-2xl bg-white/70 flex items-center justify-center text-orange-500 shrink-0"><AlertTriangle size={18} strokeWidth={2.5} /></span>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold text-sorted-ink leading-tight">Use it up</p>
          <p className="text-sm text-sorted-ink mt-1" data-testid="fridge-nudge-text">{data.nudge}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {data.expiring.slice(0, 5).map((i) => (
              <button key={i.name} onClick={() => navigate("/kitchen")} data-testid={`expiring-${i.name}`}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${i.days_left <= 0 ? "bg-sorted-rose text-white" : "bg-white/80 text-sorted-ink"}`}>
                {i.name} · {label(i)}{i.guessed ? " (est.)" : ""}
              </button>
            ))}
          </div>
          {!compact && data.meal && (
            <div className="mt-3 bg-white/80 rounded-2xl p-3" data-testid="fridge-nudge-meal">
              <p className="font-bold text-sorted-ink text-sm">{data.meal.title}</p>
              <p className="text-xs text-sorted-slate">{data.meal.description}</p>
              <div className="flex items-center gap-2 mt-2">
                {data.meal.prep_time_min && <span className="text-[11px] font-bold text-sorted-slate flex items-center gap-1"><Clock size={11} strokeWidth={2.5} />{data.meal.prep_time_min} min</span>}
                {data.swap_day && (
                  <button onClick={swap} disabled={swapping} data-testid="fridge-nudge-swap"
                    className="ml-auto bg-sorted-rose text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
                    {swapping ? <Loader2 size={12} className="animate-spin" /> : <Repeat size={12} strokeWidth={2.5} />} Swap {data.swap_day.slice(0, 3)}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
